/**
 * BookingWidget — Rule-based line-item booking widget.
 *
 * Calculation types:
 *   flat              → unit_price (no inputs needed)
 *   quantity_based    → qty × unit_price
 *   person_based      → people × unit_price
 *   quantity_and_days → qty × days × unit_price  (event: qty × unit_price)
 *   person_and_days   → people × days × unit_price  (event: people × unit_price)
 *
 * User flow per product modal:
 *   1. Select mode (daily/event) + variant
 *   2. Fill in required inputs (qty / people / days / date)
 *   3. Click "Add Line" — line appears in list
 *   4. Repeat for more variant/qty combinations
 *   5. Click "Add to Cart" (in parent)
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { tr as trLocale } from 'date-fns/locale';
import { Calendar, Users, Minus, Plus, AlertCircle, Sun, Star, Trash2, Truck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_EVENT_START = '2026-11-09';
const DEFAULT_EVENT_END   = '2026-11-20';
const DEFAULT_EVENT_DAYS  = 12;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveUnitPrice(product, mode, selectedVariant) {
  if (mode === 'daily') {
    const ov = selectedVariant?.price_override_daily;
    if (ov != null) return parseFloat(ov);
    return parseFloat(product.daily_price ?? product.effective_price ?? product.price ?? 0);
  }
  if (mode === 'event') {
    const ov = selectedVariant?.price_override_event;
    if (ov != null) return parseFloat(ov);
    return parseFloat(product.event_price ?? product.price ?? 0);
  }
  return parseFloat(product.daily_price ?? product.event_price ?? product.price ?? 0);
}

function computeLineTotal(calcType, mode, unitPrice, qty, people, days, isRental = false) {
  const q = Math.max(parseInt(qty) || 1, 1);
  const p = Math.max(parseInt(people) || 0, 0);
  const d = Math.max(parseInt(days) || 1, 1);
  // Rental: daily mode → qty × days × daily_price; event mode → qty × event_price (or flat if qty N/A)
  if (isRental && mode === 'daily') return q * d * unitPrice;
  if (isRental && mode === 'event') return q * unitPrice;
  switch (calcType) {
    case 'flat':             return unitPrice;
    case 'quantity_based':   return q * unitPrice;
    case 'carton_based':     return q * unitPrice;
    case 'person_based':
      // daily mode with days tracking (e.g. HR: kişi × gün × fiyat)
      if (mode === 'daily' && d > 0) return p * d * unitPrice;
      return p * unitPrice;
    case 'quantity_and_days':
      return mode === 'event' ? q * unitPrice : q * d * unitPrice;
    case 'person_and_days':
      return mode === 'event' ? p * unitPrice : p * d * unitPrice;
    default:                 return unitPrice;
  }
}


// ─── ForkliftSlotPicker ────────────────────────────────────────────────────────
function ForkliftSlotPicker({ product, onLinesReady, tr }) {
  const [selectedDate, setSelectedDate] = useState(DEFAULT_EVENT_START);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [fetchErr, setFetchErr] = useState('');
  const [slotError, setSlotError] = useState('');

  const forkliftLabel = useMemo(() => {
    const presets = {
      standard_1_5t: '1.5 Ton', standard_2t: '2 Ton', standard_3t: '3 Ton',
      standard_5t: '5 Ton', standard_7t: '7 Ton',
    };
    if (product.forklift_type === 'custom' && product.forklift_tonnage) return `${product.forklift_tonnage} Ton (Özel)`;
    return presets[product.forklift_type] || product.forklift_type;
  }, [product]);

  const fetchSlots = useCallback(async (date) => {
    if (!date) return;
    setLoading(true); setFetchErr(''); setSlots([]);
    try {
      const token = localStorage.getItem('cop31_token') || localStorage.getItem('token') || '';
      const res = await axios.get(
        `${API}/api/customer/supplier-products/${product.id}/forklift-slots?date=${date}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setSlots(res.data.slots || []);
    } catch { setFetchErr(tr ? 'Slotlar yüklenemedi.' : 'Could not load slots.'); }
    setLoading(false);
  }, [product.id, tr]);

  useEffect(() => { fetchSlots(selectedDate); }, [selectedDate, fetchSlots]);

  // Ardışıklık kontrolü
  const isConsecutive = (hours) => {
    if (hours.length <= 1) return true;
    const sorted = [...hours].sort((a, b) => a - b);
    return sorted.every((h, i) => i === 0 || h === sorted[i - 1] + 1);
  };

  const toggleSlot = (slot) => {
    if (slot.is_full) return;
    setSlotError('');
    const exists = selectedSlots.find(s => s.hour === slot.hour);
    if (exists) {
      // Kaldırma: kalan slotlar ardışık mı?
      const remaining = selectedSlots.filter(s => s.hour !== slot.hour);
      if (remaining.length > 0 && !isConsecutive(remaining.map(s => s.hour))) {
        setSlotError(tr
          ? 'Ortadaki saati kaldıramazsınız — önce son saati kaldırın.'
          : 'Cannot remove middle slot — remove from the end first.');
        return;
      }
      setSelectedSlots(remaining);
    } else {
      // Ekleme: yeni set ardışık mı?
      const newHours = [...selectedSlots.map(s => s.hour), slot.hour];
      if (!isConsecutive(newHours)) {
        setSlotError(tr
          ? 'Yalnızca ardışık saatler seçilebilir. Farklı bir zaman bloğu için sepete ekleyin ve tekrar seçin.'
          : 'Only consecutive hours allowed. To book a different block, add current selection first, then select new slots.');
        return;
      }
      setSelectedSlots(prev => [...prev, slot]);
    }
  };

  useEffect(() => {
    const hourly = product.hourly_price || product.daily_price || product.price || 0;
    const lines = selectedSlots.map(slot => ({
      _key: `${selectedDate}-${slot.hour}`,
      product_id: product.id,
      product_name: product.title,
      slot_date: selectedDate,
      slot_hour: slot.hour,
      quantity: 1,
      unit_price: hourly,
      line_total: hourly,
      pricing_mode_selected: 'hourly',
      calculation_type: 'flat',
      currency: product.currency || 'USD',
      number_of_days: 1,
      label: `${selectedDate} · ${slot.label}`,
    }));
    onLinesReady(lines);
  }, [selectedSlots, selectedDate]); // eslint-disable-line

  const hourly = product.hourly_price || product.daily_price || product.price || 0;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 bg-blue-50 rounded-xl px-3 py-2">
        <Truck className="h-4 w-4 text-blue-500 shrink-0" />
        <div className="flex-1">
          <span className="text-xs font-semibold text-blue-800">Standart Forklift {forkliftLabel}</span>
          <span className="text-xs text-blue-600 ml-2">· {product.stock_quantity || 1} {tr ? 'araç mevcut' : 'units available'}</span>
        </div>
        <span className="text-xs font-bold text-blue-700">{product.currency || 'USD'} {hourly}<span className="font-normal text-blue-500">/saat</span></span>
      </div>
      {/* Date picker */}
      <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
        <Calendar className="h-3 w-3 text-gray-400 shrink-0" />
        <span className="text-xs text-gray-400 shrink-0">{tr ? 'Tarih' : 'Date'}</span>
        <input type="date" value={selectedDate}
          min={DEFAULT_EVENT_START} max={DEFAULT_EVENT_END}
          onChange={e => { setSelectedDate(e.target.value); setSelectedSlots([]); }}
          className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white focus:outline-none focus:border-blue-400" />
      </div>
      {/* Slots */}
      {loading && <div className="text-center py-3 text-xs text-gray-400">{tr ? 'Yükleniyor...' : 'Loading...'}</div>}
      {fetchErr && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{fetchErr}</p>}
      {!loading && slots.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] text-gray-400 uppercase font-semibold">
            {product.operating_hours_start || '08:00'} – {product.operating_hours_end || '18:00'}
          </p>
          {slots.map(slot => {
            const isSelected = selectedSlots.some(s => s.hour === slot.hour);
            return (
              <button key={slot.hour} type="button" disabled={slot.is_full}
                onClick={() => toggleSlot(slot)}
                className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 border-2 transition-all ${
                  slot.is_full      ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed' :
                  isSelected        ? 'border-blue-500 bg-blue-50 text-blue-800' :
                                      'border-gray-100 bg-white hover:border-blue-200 text-gray-700'
                }`}>
                <span className="text-xs font-mono font-semibold w-28 shrink-0">{slot.label}</span>
                <div className="flex gap-0.5">
                  {Array.from({ length: Math.min(slot.total, 10) }).map((_, i) => (
                    <div key={i} className={`h-2 w-2 rounded-full ${
                      i < (slot.total - slot.available) ? 'bg-red-400' :
                      isSelected ? 'bg-blue-400' : 'bg-emerald-300'
                    }`} />
                  ))}
                </div>
                <span className={`text-xs ml-auto shrink-0 ${
                  slot.is_full ? 'text-red-400 font-semibold' :
                  slot.available < slot.total ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {slot.is_full ? (tr ? 'DOLU' : 'FULL') : `${slot.available}/${slot.total} ${tr ? 'müsait' : 'open'}`}
                </span>
                {isSelected && <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded-full">✓</span>}
              </button>
            );
          })}
          {/* Ardışık olmayan seçim uyarısı */}
          {slotError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 mt-1">
              <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-600 font-medium">{slotError}</p>
            </div>
          )}
        </div>
      )}
      {/* Selected summary */}
      {selectedSlots.length > 0 && (
        <div className="bg-blue-50 rounded-xl px-3 py-2">
          <p className="text-xs font-semibold text-blue-700">
            {selectedSlots.length} {tr ? 'saat seçildi' : 'slots selected'} · {product.currency || 'USD'} {(selectedSlots.length * hourly).toLocaleString()}
          </p>
          <div className="flex flex-wrap gap-1 mt-1">
            {[...selectedSlots].sort((a,b) => a.hour - b.hour).map(s => (
              <span key={s.hour} className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{s.label}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BookingWidget({ product, language, onChange, onVariantChange, onAddToCart, addingToCart, eventConfig }) {
  const tr = language === 'tr';
  const currency = product.currency || 'USD';
  const calcType = product.calculation_type || 'flat';
  const supported = product.pricing_mode_supported || 'both';
  const productType = product.product_type || 'rental';
  const isRental = productType === 'rental' || product.service_pricing_type === 'daily';
  const isService = productType === 'service';
  const isForklift = !!product.forklift_type; // Forklift/Logistic product

  // ── Forklift lines state ──────────────────────────────────────────────────────
  const [forkliftLines, setForkliftLines] = useState([]);
  const forkliftTotal = forkliftLines.reduce((s, l) => s + (l.line_total || 0), 0);

  // Min / Max quantity helpers — must come before useState so initial qty is correct
  const isCartonBased = calcType === 'carton_based';
  const ipc = product.items_per_carton || 1;
  const minQty = Math.max(1, product.min_order_quantity || 1);
  // For variant-selected stock enforcement — use variant stock if available
  const stockDerivedMax = (() => {
    const hasStockPolicy = product.stock_policy && product.stock_policy !== 'untracked';
    return null; // resolved later after selectedVariant is available
  })();

  const eventStart = eventConfig?.event_start || DEFAULT_EVENT_START;
  const eventEnd   = eventConfig?.event_end   || DEFAULT_EVENT_END;
  const eventDays  = eventConfig?.event_days  || DEFAULT_EVENT_DAYS;

  const hasDaily = supported === 'daily' || supported === 'both';
  // hasEvent: rentals with event mode OR services with both modes (e.g. HR: daily price + event price)
  const hasEvent = (isRental || (isService && supported === 'both'))
                && (supported === 'event' || supported === 'both');

  // Real-price check: user may set supported='both' but only fill event_price (leaving daily null)
  const hasDailyPrice = !!parseFloat(product.daily_price || 0);
  const hasEventPrice = !!parseFloat(product.event_price || 0);

  const defaultMode = (hasDaily && hasDailyPrice) ? 'daily' : (hasEvent && hasEventPrice ? 'event' : 'daily');

  const [mode, setMode]                   = useState(defaultMode);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [qty, setQty]                     = useState(String(minQty));

  // Notify parent when variant changes (for image syncing)
  const prevVariantRef = React.useRef(null);
  React.useEffect(() => {
    if (selectedVariant !== prevVariantRef.current) {
      prevVariantRef.current = selectedVariant;
      onVariantChange?.(selectedVariant);
    }
  }, [selectedVariant, onVariantChange]);

  // maxQty: take the more restrictive of max_order_quantity and variant/product stock
  const effectiveStockQty = selectedVariant?.stock ?? product.stock_quantity;
  const resolvedStockMax = (product.stock_policy && product.stock_policy !== 'untracked' && effectiveStockQty != null)
    ? effectiveStockQty : null;
  const maxQty = product.max_order_quantity
    ? (resolvedStockMax !== null ? Math.min(product.max_order_quantity, resolvedStockMax) : product.max_order_quantity)
    : resolvedStockMax;
  const [people, setPeople]               = useState('');
  const [days, setDays]                   = useState('');
  const [startDate, setStartDate]         = useState(null);
  const [lines, setLines]                 = useState([]);
  const [draftErr, setDraftErr]           = useState('');

  const isEventOnly = isRental && hasEventPrice && !hasDailyPrice; // Sadece etkinlik fiyatı var (daily_price yok veya 0)

  // Input visibility rules
  const needsQty    = ['quantity_based', 'quantity_and_days', 'carton_based'].includes(calcType);
  const needsPeople = ['person_based', 'person_and_days'].includes(calcType);
  // needsDays: rental daily, quantity/person_and_days daily, OR service daily mode
  // Event-only rental (sadece etkinlik fiyatı) → günler etkinlik süresine sabit, input gösterme
  const needsDays   = !isEventOnly && (
                       (mode === 'daily' && ['quantity_and_days', 'person_and_days'].includes(calcType))
                    || (isRental && mode === 'daily')
                    || (isService && mode === 'daily' && ['person_based', 'person_and_days'].includes(calcType))
                    );
  const needsDate   = needsDays;

  // Simple product: no variants + not service + no days required → auto-sync qty → cart
  // Event-only rental is always "simple" → auto-add to cart with event duration
  const isSimple = (!isService && !(product.variants?.length > 0) && !needsDays) || isEventOnly;

  const unitPrice = useMemo(
    () => resolveUnitPrice(product, mode, selectedVariant),
    [product, mode, selectedVariant]
  );

  // Max days: distance from selected startDate to event end (inclusive). Prevents over-booking.
  const maxDays = useMemo(() => {
    if (!startDate) return eventDays; // no start date yet → use full event length as soft cap
    const end = new Date(eventEnd + 'T00:00:00');
    const diff = Math.round((end - startDate) / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, diff);
  }, [startDate, eventEnd, eventDays]);

  const effectiveDays = mode === 'event' ? eventDays : (parseInt(days) || 0);
  const draftTotal = useMemo(() => {
    // If days-based mode and user hasn't entered any days yet → show 0
    const daysInt = parseInt(days) || 0;
    const needsDays = isRental || calcType === 'quantity_and_days' || calcType === 'person_and_days' || calcType === 'person_based';
    if (mode === 'daily' && needsDays && daysInt === 0) return 0;
    return computeLineTotal(calcType, mode, unitPrice, qty, people, effectiveDays, isRental);
  }, [calcType, mode, unitPrice, qty, people, effectiveDays, isRental, days]);

  const notifyParent = useCallback((newLines) => onChange?.(newLines), [onChange]);

  // Forklift: propagate selected slot lines to parent
  useEffect(() => {
    if (!isForklift) return;
    onChange?.(forkliftLines);
  }, [forkliftLines]); // eslint-disable-line

  // For simple products (no variants, non-service), auto-sync with parent whenever qty/mode changes
  useEffect(() => {
    if (!isSimple) return;
    const q = Math.max(parseInt(qty) || minQty, minQty);
    const lt = computeLineTotal(calcType, mode, unitPrice, q, 0, null, isRental);
    notifyParent([{
      _key: 'auto',
      pricing_mode_selected: mode,
      calculation_type:      calcType,
      variant_id: null, variant_name: null,
      quantity: q,
      number_of_people: null,
      number_of_days: isEventOnly ? eventDays : null,
      start_date: isEventOnly ? eventStart : null,
      end_date:   isEventOnly ? eventEnd   : null,
      unit_price: unitPrice, line_total: lt, currency,
      items_per_carton: isCartonBased ? (product.items_per_carton || null) : null,
    }]);
  }, [qty, mode, unitPrice, isSimple]); 

  const switchMode = (m) => {
    setMode(m);
    setQty(''); setPeople(''); setDays(''); setStartDate(null); setDraftErr('');
  };

  const validate = () => {
    const qVal = parseInt(qty) || 0;
    if (needsQty && qVal < minQty)
      return tr
        ? `Minimum sipariş: ${minQty} ${isCartonBased ? 'koli' : 'adet'}.`
        : `Min. order: ${minQty} ${isCartonBased ? 'Pack' : 'pcs'}.`;
    if (needsQty && maxQty !== null && qVal > maxQty)
      return tr
        ? `Maksimum stok: ${maxQty} ${isCartonBased ? 'koli' : 'adet'}.`
        : `Max stock: ${maxQty} ${isCartonBased ? 'Pack' : 'pcs'}.`;
    if (needsPeople && (parseInt(people) || 0) < 1) return tr ? 'Kişi sayısını giriniz.'  : 'Enter number of people.';
    if (needsDays   && (parseInt(days)   || 0) < 1) return tr ? 'Gün sayısını giriniz.'   : 'Enter number of days.';
    if (needsDays   && parseInt(days) > maxDays)
      return tr
        ? `En fazla ${maxDays} gün seçebilirsiniz (etkinlik ${new Date(eventEnd + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} tarihinde bitiyor).`
        : `Max ${maxDays} day(s) allowed (event ends ${eventEnd}).`;
    if (needsDate   && !startDate)                   return tr ? 'Başlangıç tarihi seçiniz.' : 'Select a start date.';
    return null;
  };

  const handleAddLine = () => {
    const err = validate();
    if (err) { setDraftErr(err); return; }
    setDraftErr('');

    const q  = needsQty    ? Math.max(minQty, parseInt(qty) || minQty)    : 1;
    const p  = needsPeople ? (parseInt(people) || 0) : null;
    const d  = mode === 'event' ? eventDays : (needsDays ? (parseInt(days) || 1) : null);
    const sd = startDate ? format(startDate, 'yyyy-MM-dd') : (mode === 'event' ? eventStart : null);
    const ed = (startDate && d && mode === 'daily')
      ? format(addDays(startDate, d - 1), 'yyyy-MM-dd')
      : (mode === 'event' ? eventEnd : null);
    const lt = computeLineTotal(calcType, mode, unitPrice, q, p, d || eventDays);

    const newLine = {
      _key:                 Date.now() + Math.random(),
      pricing_mode_selected: mode,
      calculation_type:     calcType,
      variant_id:           selectedVariant?.id   || null,
      variant_name:         selectedVariant?.name || null,
      quantity:             q,
      number_of_people:     p,
      number_of_days:       d,
      start_date:           sd,
      end_date:             ed,
      unit_price:           unitPrice,
      line_total:           lt,
      currency,
      // Carton-specific
      items_per_carton:     isCartonBased ? (product.items_per_carton || null) : null,
    };
    const newLines = [...lines, newLine];
    setLines(newLines);
    notifyParent(newLines);
    setQty(''); setPeople(''); setDays(''); setStartDate(null);
  };

  const handleRemoveLine = (key) => {
    const newLines = lines.filter(l => l._key !== key);
    setLines(newLines);
    notifyParent(newLines);
  };

  const linesTotal = lines.reduce((s, l) => s + (l.line_total || 0), 0);

  const lineDesc = (l) => {
    const parts = [];
    if (l.calculation_type === 'carton_based') {
      parts.push(`${l.quantity} ${tr ? 'koli' : 'Pack'}`);
      if (l.items_per_carton) parts.push(`(${l.items_per_carton} ${tr ? 'adet/koli' : 'units/Pack'})`);
      return parts.join(' ');
    }
    if (l.pricing_mode_selected === 'daily')  parts.push(tr ? 'Günlük' : 'Daily');
    if (l.pricing_mode_selected === 'event')  parts.push(tr ? 'Etkinlik' : 'Event');
    if (l.variant_name)                       parts.push(l.variant_name);
    if (needsQty && l.quantity  > 0)          parts.push(`${l.quantity} ${tr ? 'adet' : 'pcs'}`);
    if (l.number_of_people)                   parts.push(`${l.number_of_people} ${tr ? 'kişi' : 'ppl'}`);
    if (l.number_of_days && l.pricing_mode_selected === 'daily') parts.push(`${l.number_of_days} ${tr ? 'gün' : 'days'}`);
    return parts.filter(Boolean).join(' · ');
  };

  if (!mode) return null;

  // ── Forklift mode: slot picker UI ──────────────────────────────────────────
  if (isForklift) {
    return (
      <div className="space-y-4">
        <ForkliftSlotPicker product={product} onLinesReady={setForkliftLines} tr={tr} />
        {/* Lines summary */}
        {forkliftLines.length > 0 && (
          <div className="bg-gray-50 rounded-xl px-3 py-2 flex items-center justify-between text-sm">
            <span className="text-gray-500">{tr ? 'Toplam' : 'Total'}:</span>
            <span className="font-bold text-gray-800">{product.currency || 'USD'} {forkliftTotal.toLocaleString()}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Variant selector ────────────────────────────────────────── */}
      {product.variants?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">{tr ? 'Varyant' : 'Variant'}</p>
          <div className="flex flex-wrap gap-2">
            {/* Original / default option */}
            <button
              data-testid="variant-btn-original"
              onClick={() => setSelectedVariant(null)}
              className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all ${
                !selectedVariant ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-gray-200 hover:border-gray-300'
              }`}>
              {tr ? 'Orijinal' : 'Original'}
            </button>
            {product.variants.map(v => {
              const vPrice = mode === 'daily'
                ? (v.price_override_daily ?? v.price)
                : (v.price_override_event ?? v.price);
              const isActive = selectedVariant?.id === v.id;
              return (
                <button key={v.id}
                  data-testid={`variant-btn-${v.id}`}
                  onClick={() => setSelectedVariant(isActive ? null : v)}
                  className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all ${
                    isActive ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  {v.name}
                  {vPrice != null && (
                    <span className="ml-1.5 text-[11px] text-emerald-600 font-bold">{currency} {vPrice}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Mode selector (rental with both modes only — and both prices actually set) ─────────────── */}
      {hasDaily && hasEvent && hasDailyPrice && hasEventPrice && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
            {isRental
              ? (tr ? 'Kiralama Türü' : 'Pricing Mode')
              : (tr ? 'Fiyatlandırma Türü' : 'Pricing Mode')
            }
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="mode-btn-daily" onClick={() => switchMode('daily')}
              className={`rounded-2xl p-3 border-2 text-left transition-all ${mode === 'daily' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100 hover:border-gray-200 bg-white'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Sun className={`h-4 w-4 ${mode === 'daily' ? 'text-emerald-600' : 'text-gray-400'}`} />
                <span className={`text-sm font-semibold ${mode === 'daily' ? 'text-emerald-700' : 'text-gray-700'}`}>
                  {tr ? 'Günlük' : 'Daily'}
                </span>
              </div>
              <p className={`text-sm font-bold ${mode === 'daily' ? 'text-emerald-600' : 'text-gray-500'}`}>
                {currency} {resolveUnitPrice(product, 'daily', selectedVariant)}
                <span className="text-xs font-normal ml-0.5">/{tr ? 'gün' : 'day'}</span>
              </p>
            </button>
            <button data-testid="mode-btn-event" onClick={() => switchMode('event')}
              className={`rounded-2xl p-3 border-2 text-left transition-all ${mode === 'event' ? 'border-amber-500 bg-amber-50' : 'border-gray-100 hover:border-gray-200 bg-white'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Star className={`h-4 w-4 ${mode === 'event' ? 'text-amber-600' : 'text-gray-400'}`} />
                <span className={`text-sm font-semibold ${mode === 'event' ? 'text-amber-700' : 'text-gray-700'}`}>
                  {tr ? 'Etkinlik' : 'Event'}
                </span>
              </div>
              <p className={`text-sm font-bold ${mode === 'event' ? 'text-amber-600' : 'text-gray-500'}`}>
                {currency} {resolveUnitPrice(product, 'event', selectedVariant)}
                <span className="text-xs font-normal ml-0.5">{tr ? ' sabit' : ' flat'}</span>
              </p>
            </button>
          </div>
        </div>
      )}

      {/* ── Price info for service / for_sale (no mode selector) ─────── */}
      {!isRental && !(hasDaily && hasEvent) && unitPrice > 0 && (
        <div data-testid="price-info-banner" className="bg-gray-50 rounded-2xl p-3.5 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {productType === 'for_sale'
              ? (tr ? 'Satış Fiyatı' : 'Sale Price')
              : (tr ? 'Hizmet Fiyatı' : 'Service Price')}
          </span>
          <span className="font-bold text-gray-900">{currency} {unitPrice.toLocaleString('tr-TR')}</span>
        </div>
      )}

      {/* ── Event mode info banner ──────────────────────────────────── */}
      {mode === 'event' && (
        <div className="bg-amber-50 rounded-2xl p-3.5 border border-amber-200 flex items-start gap-3">
          <Star className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">{tr ? 'Tüm Etkinlik Süresi' : 'Full Event Duration'}</p>
            <p className="text-xs text-amber-600 mt-0.5">
              {tr
                ? `${new Date(eventStart + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} — ${new Date(eventEnd + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })} · ${eventDays} gün`
                : `${eventStart} — ${eventEnd} · ${eventDays} days`}
            </p>
          </div>
        </div>
      )}

      {/* ── Inputs ─────────────────────────────────────────────────── */}
      <div className="space-y-2">

        {/* Quantity / Carton */}
        {needsQty && (
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
            {isCartonBased && product.items_per_carton && (
              <span className="text-[10px] text-orange-600 bg-orange-100 rounded px-1.5 py-0.5 shrink-0">
                1={product.items_per_carton}u
              </span>
            )}
            <span className="text-xs text-gray-400 shrink-0">
              {isCartonBased ? (tr ? 'Koli' : 'Pack') : (tr ? 'Adet' : 'Qty')}
            </span>
            <button onClick={() => setQty(q => String(Math.max(minQty, (parseInt(q) || minQty) - 1)))}
              className="w-6 h-6 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-white text-gray-500 shrink-0">
              <Minus className="h-3 w-3" />
            </button>
            <input data-testid="booking-quantity" type="number" min={minQty} max={maxQty || undefined} value={qty}
              onChange={e => {
                const raw = e.target.value;
                const v = parseInt(raw);
                if (!isNaN(v) && maxQty !== null && v > maxQty) { setQty(String(maxQty)); setDraftErr(tr ? `Maks: ${maxQty}` : `Max: ${maxQty}`); }
                else if (!isNaN(v) && v < minQty && raw.length >= String(minQty).length) { setQty(String(minQty)); setDraftErr(tr ? `Min: ${minQty}` : `Min: ${minQty}`); }
                else { setQty(raw); setDraftErr(''); }
              }}
              onBlur={() => { const v = parseInt(qty) || minQty; const c = Math.max(minQty, maxQty !== null ? Math.min(v, maxQty) : v); setQty(String(c)); }}
              placeholder={String(minQty)}
              className="w-14 text-center rounded-lg border border-gray-200 px-1 py-1 text-sm font-bold focus:outline-none focus:border-emerald-500 bg-white" />
            <button onClick={() => setQty(q => String(Math.min(maxQty !== null ? maxQty : Infinity, (parseInt(q) || 0) + 1)))}
              className="w-6 h-6 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-white text-gray-500 shrink-0">
              <Plus className="h-3 w-3" />
            </button>
            {isCartonBased && product.items_per_carton && (parseInt(qty) || 0) > 0 && (
              <span className="text-xs text-gray-400 ml-1">= {(parseInt(qty) || 0) * product.items_per_carton} {tr ? 'adet' : 'u'}</span>
            )}
            <div className="flex gap-1.5 ml-auto">
              {minQty > 1 && <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">min {minQty}</span>}
              {maxQty !== null && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">max {maxQty}</span>}
            </div>
          </div>
        )}

        {/* People + Days — compact inline grid */}
        {(needsPeople || needsDays) && (
          <div className="flex gap-2">
            {needsPeople && (
              <div className="flex items-center gap-1.5 bg-gray-50 rounded-xl px-3 py-2 flex-1">
                <Users className="h-3 w-3 text-gray-400 shrink-0" />
                <span className="text-xs text-gray-400 shrink-0">{tr ? 'Kişi' : 'Ppl'}</span>
                <button onClick={() => setPeople(p => String(Math.max(product.minimum_people || 1, (parseInt(p) || 0) - 1)))}
                  className="w-6 h-6 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-white text-gray-500 shrink-0">
                  <Minus className="h-3 w-3" />
                </button>
                <input data-testid="booking-number-of-people" type="number"
                  min={product.minimum_people || 1} max={product.maximum_people || undefined}
                  value={people} onChange={e => { setPeople(e.target.value); setDraftErr(''); }}
                  placeholder={String(product.minimum_people || 1)}
                  className="w-12 text-center rounded-lg border border-gray-200 px-1 py-1 text-sm font-bold focus:outline-none focus:border-emerald-500 bg-white" />
                <button onClick={() => setPeople(p => String((parseInt(p) || 0) + 1))}
                  className="w-6 h-6 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-white text-gray-500 shrink-0">
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            )}
            {needsDays && (
              <div className="flex items-center gap-1.5 bg-gray-50 rounded-xl px-3 py-2 flex-1">
                <Calendar className="h-3 w-3 text-gray-400 shrink-0" />
                <span className="text-xs text-gray-400 shrink-0">{tr ? 'Gün' : 'Days'}</span>
                <button onClick={() => setDays(d => String(Math.max(1, (parseInt(d) || 0) - 1)))}
                  className="w-6 h-6 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-white text-gray-500 shrink-0">
                  <Minus className="h-3 w-3" />
                </button>
                <input data-testid="booking-number-of-days" type="number" min="1" max={maxDays} value={days}
                  onChange={e => {
                    const v = parseInt(e.target.value);
                    if (!isNaN(v) && v > maxDays) { setDays(String(maxDays)); setDraftErr(tr ? `Maks: ${maxDays} gün` : `Max: ${maxDays} days`); }
                    else { setDays(e.target.value); setDraftErr(''); }
                  }}
                  onBlur={() => { const v = parseInt(days) || 0; if (v > maxDays) setDays(String(maxDays)); }}
                  placeholder="0"
                  className="w-12 text-center rounded-lg border border-gray-200 px-1 py-1 text-sm font-bold focus:outline-none focus:border-emerald-500 bg-white" />
                <button onClick={() => setDays(d => String(Math.min(maxDays, (parseInt(d) || 0) + 1)))}
                  className="w-6 h-6 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-white text-gray-500 shrink-0">
                  <Plus className="h-3 w-3" />
                </button>
                {startDate && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded ml-1">maks {maxDays}</span>}
              </div>
            )}
          </div>
        )}

        {/* Date picker + Add Line — same row */}
        {needsDate && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 flex-1">
              <Calendar className="h-3 w-3 text-gray-400 shrink-0" />
              <span className="text-xs text-gray-400 shrink-0">{tr ? 'Başlangıç' : 'Start'}</span>
              <input data-testid="booking-start-date" type="date"
                value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                min={eventStart} max={eventEnd}
                onChange={e => {
                  const newDate = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                  setStartDate(newDate);
                  // Auto-clip days to remaining event days
                  if (newDate) {
                    const end = new Date(eventEnd + 'T00:00:00');
                    const newMax = Math.max(1, Math.round((end - newDate) / (1000 * 60 * 60 * 24)) + 1);
                    if ((parseInt(days) || 0) > newMax) setDays(String(newMax));
                  }
                  setDraftErr('');
                }}
                className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:border-emerald-500 bg-white" />
              {startDate && parseInt(days) >= 1 && (
                <span className="text-xs text-emerald-600 font-medium shrink-0">
                  → {format(addDays(startDate, (parseInt(days)||1) - 1), 'dd MMM', { locale: trLocale })}
                </span>
              )}
            </div>
            {!isSimple && (
              <button data-testid="booking-add-line-btn"
                onClick={handleAddLine}
                className="rounded-xl px-4 py-2 text-xs font-semibold border border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-all whitespace-nowrap shrink-0">
                + {tr ? 'Ekle' : 'Add'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Draft preview & error ───────────────────────────────────── */}
      {draftTotal > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-xs text-gray-400">
            {isSimple ? (tr ? 'Toplam:' : 'Total:') : (tr ? 'Bu satır:' : 'This line:')}
          </span>
          <span className="font-bold text-gray-800 text-sm">{currency} {draftTotal.toLocaleString('tr-TR', { minimumFractionDigits: 0 })}</span>
        </div>
      )}
      {draftErr && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3 shrink-0" /> {draftErr}
        </p>
      )}

      {/* ── Add Line button — only shown when no date row (event mode / qty) */}
      {!isSimple && !needsDate && (
        <button data-testid="booking-add-line-secondary"
          onClick={handleAddLine}
          className="w-full rounded-xl py-2.5 text-sm font-semibold border-2 border-dashed border-emerald-300 text-emerald-600 hover:bg-emerald-50 transition-all">
          + {tr ? 'Satır Ekle' : 'Add Line'}
        </button>
      )}

      {/* ── Lines list ───────────────────────────────────────────────── */}
      {!isSimple && lines.length > 0 && (
        <div className="space-y-1.5" data-testid="booking-lines-list">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{tr ? 'Eklenen Seçenekler' : 'Added Lines'}</p>
          {lines.map(l => (
            <div key={l._key} className="bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-emerald-800 truncate">{lineDesc(l)}</p>
                <p className="text-[11px] text-emerald-600">
                  {currency} {l.unit_price}
                  {(needsQty && l.quantity > 1) && ` × ${l.quantity}`}
                  {l.number_of_people ? ` × ${l.number_of_people} ${tr ? 'kişi' : 'ppl'}` : ''}
                  {(l.number_of_days && l.pricing_mode_selected === 'daily') ? ` × ${l.number_of_days} ${tr ? 'gün' : 'days'}` : ''}
                </p>
              </div>
              <span className="font-bold text-emerald-700 shrink-0 text-sm">
                {currency} {(l.line_total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })}
              </span>
              <button data-testid={`remove-line-${l._key}`} onClick={() => handleRemoveLine(l._key)}
                className="text-red-300 hover:text-red-500 p-1 shrink-0">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {/* Total bar */}
          <div className="bg-gray-900 text-white rounded-2xl px-4 py-3 flex items-center justify-between" data-testid="booking-lines-total">
            <div>
              <p className="text-xs text-gray-400 uppercase">{tr ? 'Toplam' : 'Total'}</p>
              <p className="text-[11px] text-gray-500">{lines.length} {tr ? 'seçenek' : 'line(s)'}</p>
            </div>
            <p className="text-xl font-extrabold text-emerald-400">
              {currency} {linesTotal.toLocaleString('tr-TR', { minimumFractionDigits: 0 })}
            </p>
          </div>
        </div>
      )}

      {/* ── Sepete Ekle — only when there are committed lines ────────── */}
      {!isSimple && lines.length > 0 && onAddToCart && (
        <button
          data-testid="booking-direct-add-to-cart"
          disabled={addingToCart}
          onClick={() => onAddToCart(lines)}
          className="w-full rounded-2xl py-3 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60">
          {addingToCart
            ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'Ekleniyor...' : 'Adding...'}</>
            : <><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m1.6 8a2 2 0 100 4 2 2 0 000-4zm10 0a2 2 0 100 4 2 2 0 000-4z" /></svg>
              {tr ? `Sepete Ekle (${lines.length})` : `Add to Cart (${lines.length})`}</>}
        </button>
      )}
    </div>
  );
}
